import { ReactNode } from "react";
import { formatRelative } from "@project-mgmt/utils";

/**
 * Pledge data structure with backer information
 * Matches the API response from GET /api/ventures/:id/pledges
 */
export interface PledgeWithBacker {
  id: string;
  ventureId: string;
  backerId: string;
  amount: number;
  status: string;
  pledgedAt: string;
  backerName: string | null;
}

interface PledgeListItemProps {
  pledge: PledgeWithBacker;
}

/**
 * PledgeListItem component displays individual pledge information
 * with backer details, amount, and timestamp.
 *
 * Features:
 * - Displays backer avatar placeholder icon
 * - Shows backer name with "Anonymous Backer" fallback
 * - Formats pledge amount as currency (USD)
 * - Shows relative time (e.g., "2 days ago")
 * - White background with gray border
 * - Responsive for mobile devices
 *
 * Requirements: 3.2, 3.3, 6.2, 6.3
 */
export function PledgeListItem({ pledge }: PledgeListItemProps): ReactNode {
  // Format currency using USD locale
  const formattedAmount = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(pledge.amount);

  // Format timestamp as relative time
  const relativeTime = formatRelative(pledge.pledgedAt);

  // Use backer name or fallback to "Anonymous Backer"
  const displayName = pledge.backerName || "Anonymous Backer";

  return (
    <div className="flex items-center gap-3 sm:gap-4 rounded-lg border border-gray-200 bg-white p-3 sm:p-4 transition-shadow hover:shadow-sm">
      {/* Backer avatar placeholder */}
      <div
        className="flex h-10 w-10 sm:h-12 sm:w-12 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600"
        aria-hidden="true"
      >
        <svg
          className="h-5 w-5 sm:h-6 sm:w-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
      </div>

      {/* Pledge information */}
      <div className="flex-1 min-w-0">
        {/* Backer name */}
        <div className="text-sm sm:text-base font-medium text-black truncate">{displayName}</div>

        {/* Amount and timestamp */}
        <div className="mt-1 flex flex-wrap items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-gray-600">
          <span className="font-semibold text-black">{formattedAmount}</span>
          <span aria-hidden="true">•</span>
          <time dateTime={pledge.pledgedAt} className="whitespace-nowrap">
            {relativeTime}
          </time>
        </div>
      </div>
    </div>
  );
}
